/**
 * Deals-gap W2: return-URL persistence for the deal-creation training block.
 *
 * When a blocked BDM clicks [Start/Finish Training Now] we remember where they
 * came from; once their Level 1 Certification is complete, the training player
 * offers a "Continue registering your deal" CTA back to deal creation.
 *
 * sessionStorage (not localStorage) on purpose: the intent is per-tab and
 * should not survive the session. All access is try/catch-guarded so private
 * browsing / storage-disabled environments degrade to "no return CTA".
 */

const KEY = "prm-training-return";

export interface TrainingReturnTarget {
  /** The only supported destination today — the deal-registration wizard. */
  to: "/dashboard/partner/deals/new";
  /** Draft-resume context, when the block interrupted a ?draftId deep-link. */
  draftId?: string;
}

export function rememberTrainingReturn(target: TrainingReturnTarget): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(target));
  } catch {
    // Storage unavailable — silently skip; the user just loses the CTA.
  }
}

export function peekTrainingReturn(): TrainingReturnTarget | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TrainingReturnTarget>;
    // Validate rather than trust — sessionStorage is user-editable.
    return parsed.to === "/dashboard/partner/deals/new"
      ? {
          to: parsed.to,
          ...(typeof parsed.draftId === "string" ? { draftId: parsed.draftId } : {}),
        }
      : null;
  } catch {
    return null;
  }
}

export function clearTrainingReturn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
